// Skin builder dispatch — re-exports all skin utilities and individual builders,
// and provides the main buildSkinMesh entry point.

import * as THREE from 'three';
import { fppHeadGroup } from './skin-utils';
import { buildDefaultT, buildDefaultCT } from './skins-default';
import { buildDiamondHands, buildPaperHands, buildLaserEyes, buildMoonboy } from './skins-crypto';
import { buildTheBull, buildTheBear, buildTheWhale, buildRugPuller } from './skins-market';
import {
  buildTheDoomer, buildTheBloomer, buildTheChad, buildTheNPC,
  buildCryptoAnon, buildPepe, buildTheShiba, buildGigaChad,
} from './skins-meme';
import {
  buildDogecoin, buildSatoshi, buildMemelord, buildBonk, buildDogwifhat,
  buildFloki, buildMog, buildBrett, buildPopcat, buildSpx6900,
} from './skins-memecoins';

export * from './skin-utils';
export * from './skins-default';
export * from './skins-crypto';
export * from './skins-market';
export * from './skins-meme';
export * from './skins-memecoins';

/* ------------------------------------------------------------------ */
/*  Main skin builder dispatch                                         */
/* ------------------------------------------------------------------ */

/**
 * Auto-tag head-level meshes for FPP hiding.
 * Moves any direct-child mesh whose center Y >= HEAD_HIDE_Y into the __fpp_hidden__ group.
 * Recurses into child groups (but not into groups already named __fpp_hidden__).
 */
const HEAD_HIDE_Y = 1.48;

function autoTagHeadMeshes(group: THREE.Group): void {
  const headGroup = fppHeadGroup(group);
  const toMove: THREE.Object3D[] = [];

  for (const child of group.children) {
    if (child === headGroup) continue;
    if (child.name === '__fpp_hidden__') continue;

    if ((child as THREE.Mesh).isMesh) {
      // Check world-space Y of the mesh
      if (child.position.y >= HEAD_HIDE_Y) {
        toMove.push(child);
      }
    } else if (child instanceof THREE.Group) {
      // Recurse into child groups to tag their head-level meshes
      autoTagHeadMeshes(child);
    }
  }

  for (const obj of toMove) {
    group.remove(obj);
    headGroup.add(obj);
  }
}

export function buildSkinMesh(skinId: number, color: number): THREE.Group {
  let result: THREE.Group;
  switch (skinId) {
    case 0: result = buildDefaultT(color); break;
    case 1: result = buildDefaultCT(color); break;
    case 2: result = buildDiamondHands(color); break;
    case 3: result = buildPaperHands(color); break;
    case 4: result = buildLaserEyes(color); break;
    case 5: result = buildMoonboy(color); break;
    case 6: result = buildTheBull(color); break;
    case 7: result = buildTheBear(color); break;
    case 8: result = buildTheWhale(color); break;
    case 9: result = buildRugPuller(color); break;
    case 10: result = buildTheDoomer(color); break;
    case 11: result = buildTheBloomer(color); break;
    case 12: result = buildTheChad(color); break;
    case 13: result = buildTheNPC(color); break;
    case 14: result = buildCryptoAnon(color); break;
    case 15: result = buildPepe(color); break;
    case 16: result = buildTheShiba(color); break;
    case 17: result = buildGigaChad(color); break;
    case 18: result = buildDogecoin(color); break;
    case 19: result = buildSatoshi(color); break;
    case 20: result = buildMemelord(color); break;
    case 21: result = buildBonk(color); break;
    case 22: result = buildDogwifhat(color); break;
    case 23: result = buildFloki(color); break;
    case 24: result = buildMog(color); break;
    case 25: result = buildBrett(color); break;
    case 26: result = buildPopcat(color); break;
    case 27: result = buildSpx6900(color); break;
    default: result = buildTheNPC(color); break;
  }
  // Auto-move head-level geometry into __fpp_hidden__ group
  autoTagHeadMeshes(result);
  return result;
}
